import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { ProjectProvider } from "@/context/ProjectContext";
import { CompanyProvider } from "@/context/CompanyContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppShell } from "@/components/layout/AppShell";
import { ProjectShell } from "@/components/layout/ProjectShell";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import Portfolio from "@/pages/Portfolio";
import Import from "@/pages/Import";
import ComingSoon from "@/pages/ComingSoon";
import Companies from "@/pages/masters/Companies";
import Branches from "@/pages/masters/Branches";
import UnitsOfMeasure from "@/pages/masters/UnitsOfMeasure";
import TaxCodes from "@/pages/masters/TaxCodes";
import NumberingSeries from "@/pages/masters/NumberingSeries";
import ApprovalWorkflows from "@/pages/masters/ApprovalWorkflows";
import ChartOfAccounts from "@/pages/gl/ChartOfAccounts";
import PostJournal from "@/pages/gl/PostJournal";
import PeriodsYearEnd from "@/pages/gl/PeriodsYearEnd";
import LedgerTallyExport from "@/pages/gl/LedgerTallyExport";
import InterCompanyLoans from "@/pages/gl/InterCompanyLoans";
import Vendors from "@/pages/procurement/Vendors";
import PurchaseIndents from "@/pages/procurement/PurchaseIndents";
import PurchaseRequisitions from "@/pages/procurement/PurchaseRequisitions";
import RFQs from "@/pages/procurement/RFQs";
import PurchaseOrders from "@/pages/procurement/PurchaseOrders";
import GoodsReceiptNotes from "@/pages/procurement/GoodsReceiptNotes";
import PurchaseBills from "@/pages/procurement/PurchaseBills";
import VendorAdvances from "@/pages/procurement/VendorAdvances";
import DebitNotes from "@/pages/procurement/DebitNotes";
import ServiceRequisitions from "@/pages/procurement/ServiceRequisitions";
import WorkOrders from "@/pages/procurement/WorkOrders";
import ContractorJMRs from "@/pages/procurement/ContractorJMRs";
import ServiceBills from "@/pages/procurement/ServiceBills";
import VendorPayments from "@/pages/procurement/VendorPayments";
import VendorLedger from "@/pages/procurement/VendorLedger";
import Items from "@/pages/inventory/Items";
import Warehouses from "@/pages/inventory/Warehouses";
import StockBalances from "@/pages/inventory/StockBalances";
import Receipts from "@/pages/inventory/Receipts";
import GoodsIssues from "@/pages/inventory/GoodsIssues";
import StoreReturns from "@/pages/inventory/StoreReturns";
import Adjustments from "@/pages/inventory/Adjustments";
import Transfers from "@/pages/inventory/Transfers";
import Clients from "@/pages/revenue/Clients";
import RABills from "@/pages/revenue/RABills";
import ClientPayments from "@/pages/revenue/ClientPayments";
import ClientLedger from "@/pages/revenue/ClientLedger";
import Dashboard from "@/pages/project/Dashboard";
import Settings from "@/pages/project/Settings";
import WBSPage from "@/pages/project/wbs/WBSPage";
import Engineering from "@/pages/project/Engineering";
import Materials from "@/pages/project/Materials";
import Construction from "@/pages/project/Construction";
import PlanVsActual from "@/pages/project/PlanVsActual";
import Alerts from "@/pages/project/Alerts";
import Issues from "@/pages/project/Issues";
import Resources from "@/pages/project/Resources";
import Schedule from "@/pages/project/Schedule";
import Audit from "@/pages/project/Audit";
import Project360 from "@/pages/project/Project360";
import ProjectFinance from "@/pages/project/ProjectFinance";
import SiteHub from "@/pages/project/SiteHub";
import Estimates from "@/pages/project/Estimates";
import CostEntries from "@/pages/project/CostEntries";
import Delays from "@/pages/project/Delays";
import DailyReports from "@/pages/project/DailyReports";
import ResourcePlanning from "@/pages/project/ResourcePlanning";
import Logbook from "@/pages/project/Logbook";
import WBSTemplates from "@/pages/setup/WBSTemplates";
import AllProjects from "@/pages/projects/AllProjects";
import AssetRegister from "@/pages/assets/AssetRegister";
import DepreciationRuns from "@/pages/assets/DepreciationRuns";
import Equipment from "@/pages/plant/Equipment";
import HireOrders from "@/pages/plant/HireOrders";
import Weighbridge from "@/pages/plant/Weighbridge";
import PlantsAndBOM from "@/pages/production/PlantsAndBOM";
import ProductionRuns from "@/pages/production/ProductionRuns";
import Dispatches from "@/pages/production/Dispatches";
import ExecutiveDashboard from "@/pages/reports/ExecutiveDashboard";
import FinancialStatements from "@/pages/reports/FinancialStatements";
import AgingReports from "@/pages/reports/AgingReports";
import CostToCompletion from "@/pages/reports/CostToCompletion";
import OperationsReports from "@/pages/reports/OperationsReports";
import Agreements from "@/pages/legal/Agreements";
import StatutoryApprovals from "@/pages/legal/StatutoryApprovals";
import CasesAndNotices from "@/pages/legal/CasesAndNotices";
import PermissionMatrix from "@/pages/admin/PermissionMatrix";
import Users from "@/pages/admin/Users";
import AuditLog from "@/pages/admin/AuditLog";
import OrgSettings from "@/pages/admin/OrgSettings";
import SystemHealth from "@/pages/admin/SystemHealth";
import IntegrityChecks from "@/pages/admin/IntegrityChecks";

function App() {
  return (
    <ThemeProvider>
    <BrandingProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <CompanyProvider>
                  <AppShell />
                </CompanyProvider>
              </ProtectedRoute>
            }
          >
            <Route index element={<Portfolio />} />
            <Route path="import" element={<Import />} />
            <Route path="erp/operations/companies" element={<Companies />} />
            <Route path="erp/operations/branches" element={<Branches />} />
            <Route path="erp/operations/units-of-measure" element={<UnitsOfMeasure />} />
            <Route path="erp/operations/tax-codes" element={<TaxCodes />} />
            <Route path="erp/operations/numbering-series" element={<NumberingSeries />} />
            <Route path="erp/operations/approval-workflows" element={<ApprovalWorkflows />} />
            <Route path="erp/finance-and-accounting/chart-of-accounts" element={<ChartOfAccounts />} />
            <Route path="erp/finance-and-accounting/post-journal" element={<PostJournal />} />
            <Route path="erp/finance-and-accounting/periods-year-end" element={<PeriodsYearEnd />} />
            <Route path="erp/finance-and-accounting/ledger-tally-export" element={<LedgerTallyExport />} />
            <Route path="erp/finance-and-accounting/periods-and-year-end" element={<PeriodsYearEnd />} />
            <Route path="erp/finance-and-accounting/ledger-and-tally-export" element={<LedgerTallyExport />} />
            <Route path="erp/finance-and-accounting/inter-company-loans" element={<InterCompanyLoans />} />
            <Route path="erp/operations/vendors" element={<Vendors />} />
            <Route path="erp/operations/purchase-indents" element={<PurchaseIndents />} />
            <Route path="erp/operations/purchase-requisitions" element={<PurchaseRequisitions />} />
            <Route path="erp/operations/rfqs" element={<RFQs />} />
            <Route path="erp/operations/purchase-orders" element={<PurchaseOrders />} />
            <Route path="erp/operations/goods-receipt-notes" element={<GoodsReceiptNotes />} />
            <Route path="erp/operations/purchase-bills" element={<PurchaseBills />} />
            <Route path="erp/operations/vendor-advances" element={<VendorAdvances />} />
            <Route path="erp/operations/debit-notes" element={<DebitNotes />} />
            <Route path="erp/operations/service-requisitions" element={<ServiceRequisitions />} />
            <Route path="erp/operations/work-orders" element={<WorkOrders />} />
            <Route path="erp/operations/contractor-jmrs" element={<ContractorJMRs />} />
            <Route path="erp/operations/service-bills" element={<ServiceBills />} />
            <Route path="erp/operations/vendor-payments" element={<VendorPayments />} />
            <Route path="erp/operations/vendor-ledger" element={<VendorLedger />} />
            <Route path="erp/operations/items" element={<Items />} />
            <Route path="erp/operations/warehouses" element={<Warehouses />} />
            <Route path="erp/operations/stock-and-balances" element={<StockBalances />} />
            <Route path="erp/operations/receipts" element={<Receipts />} />
            <Route path="erp/operations/goods-issues" element={<GoodsIssues />} />
            <Route path="erp/operations/store-returns" element={<StoreReturns />} />
            <Route path="erp/operations/adjustments" element={<Adjustments />} />
            <Route path="erp/operations/transfers" element={<Transfers />} />
            <Route path="erp/delivery/clients" element={<Clients />} />
            <Route path="erp/delivery/ra-bills" element={<RABills />} />
            <Route path="erp/delivery/client-payments" element={<ClientPayments />} />
            <Route path="erp/delivery/client-ledger" element={<ClientLedger />} />
            <Route path="erp/delivery/setup-library" element={<WBSTemplates />} />
            <Route path="erp/delivery/all-projects" element={<AllProjects />} />
            <Route path="erp/delivery/plants-and-bom" element={<PlantsAndBOM />} />
            <Route path="erp/delivery/production-runs" element={<ProductionRuns />} />
            <Route path="erp/delivery/dispatches" element={<Dispatches />} />
            <Route path="erp/assets-and-plant/asset-register" element={<AssetRegister />} />
            <Route path="erp/assets-and-plant/depreciation-runs" element={<DepreciationRuns />} />
            <Route path="erp/assets-and-plant/equipment" element={<Equipment />} />
            <Route path="erp/assets-and-plant/hire-orders" element={<HireOrders />} />
            <Route path="erp/assets-and-plant/weighbridge" element={<Weighbridge />} />
            <Route path="erp/people-and-insight/executive-dashboard" element={<ExecutiveDashboard />} />
            <Route path="erp/people-and-insight/financial-statements" element={<FinancialStatements />} />
            <Route path="erp/people-and-insight/aging-reports" element={<AgingReports />} />
            <Route path="erp/people-and-insight/cost-to-completion" element={<CostToCompletion />} />
            <Route path="erp/people-and-insight/operations-reports" element={<OperationsReports />} />
            <Route path="erp/governance/agreements" element={<Agreements />} />
            <Route path="erp/governance/statutory-approvals" element={<StatutoryApprovals />} />
            <Route path="erp/governance/cases-and-notices" element={<CasesAndNotices />} />
            <Route path="erp/governance/permission-matrix" element={<PermissionMatrix />} />
            <Route path="erp/governance/users" element={<Users />} />
            <Route path="erp/governance/audit-log" element={<AuditLog />} />
            <Route path="erp/governance/org-settings" element={<OrgSettings />} />
            <Route path="erp/governance/organization-settings" element={<OrgSettings />} />
            <Route path="erp/governance/system-health" element={<SystemHealth />} />
            <Route path="erp/governance/backup-and-health" element={<SystemHealth />} />
            <Route path="erp/governance/integrity-checks" element={<IntegrityChecks />} />
            <Route path="erp/:group/:module" element={<ComingSoon />} />

            <Route
              path="projects/:projectId"
              element={
                <ProjectProvider>
                  <ProjectShell />
                </ProjectProvider>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="settings" element={<Settings />} />
              <Route path="wbs" element={<WBSPage />} />
              <Route path="engineering" element={<Engineering />} />
              <Route path="materials" element={<Materials />} />
              <Route path="construction" element={<Construction />} />
              <Route path="plan-vs-actual" element={<PlanVsActual />} />
              <Route path="alerts" element={<Alerts />} />
              <Route path="issues" element={<Issues />} />
              <Route path="resources" element={<Resources />} />
              <Route path="schedule" element={<Schedule />} />
              <Route path="360" element={<Project360 />} />
              <Route path="finance" element={<ProjectFinance />} />
              <Route path="site-hub" element={<SiteHub />} />
              <Route path="estimates" element={<Estimates />} />
              <Route path="cost-entries" element={<CostEntries />} />
              <Route path="delays" element={<Delays />} />
              <Route path="daily-reports" element={<DailyReports />} />
              <Route path="resource-planning" element={<ResourcePlanning />} />
              <Route path="logbook" element={<Logbook />} />
              <Route path="audit" element={<Audit />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
    </BrandingProvider>
    </ThemeProvider>
  );
}

export default App;
